import type { ButtonHTMLAttributes, CSSProperties } from 'react'

/**
 * Canonical button primitive.
 *
 * Variants `primary` and `secondary` wrap the legacy `.add-btn` / `.sec-btn`
 * classes from crm/index.html — same DOM, same pixels, the API is what's new.
 *
 * `danger` uses the `.sec-btn` base PLUS a small style override (red text)
 * because the legacy host has no `.btn-danger` class. This is the first
 * variant that lets Button own a tiny piece of styling on top of the legacy
 * wrap; every future variant should justify itself the same way (real
 * consumer, no legacy equivalent, narrowest possible style surface).
 *
 * Adding variants: see shared/ui/README.md → "When a new primitive is
 * justified" + "Primitive stability."
 */

type ButtonVariant = 'primary' | 'secondary' | 'danger'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'add-btn',
  secondary: 'sec-btn',
  danger: 'sec-btn',
}

const VARIANT_STYLE: Partial<Record<ButtonVariant, CSSProperties>> = {
  danger: { color: 'var(--red)' },
}

export function Button({
  variant = 'primary',
  className,
  style,
  // Default `type="button"` so a Button nested inside a <form> doesn't
  // submit on click. Callers that need submit set type="submit" explicitly.
  type = 'button',
  ...rest
}: ButtonProps) {
  const variantClass = VARIANT_CLASS[variant]
  const mergedClass = className ? `${variantClass} ${className}` : variantClass

  // Variant style applied first; caller's `style` prop wins on any overlap.
  const variantStyle = VARIANT_STYLE[variant]
  const mergedStyle = variantStyle ? { ...variantStyle, ...style } : style

  return <button type={type} className={mergedClass} style={mergedStyle} {...rest} />
}
